import type { DraggableAttributes } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { type DragEvent, type RefObject, type TouchEvent, useRef } from 'react';

import type { Column } from './useTable/types.js';
import { isDragMotion } from '../features/ColumnOrder/column-order-dnd-utils.js';

interface UseHeaderCellDragAndDropParams {
  /**
   * Unique identifier for the header cell.
   */
  headerId: string;
  /**
   * Column instanceassociated with the header cell.
   */
  column: Column<unknown, unknown>;
  /**
   * Index of the column based on the virtualization index.
   */
  columnIndex: number;
  /**
   * Mode for column drag-and-drop functionality.
   */
  columnDragAndDropMode: 'none' | 'enabled' | 'only';
}

interface UseHeaderCellDragAndDropResult {
  /**
   * Ref to set on the draggable element.
   */
  setNodeRef: (node: HTMLElement | null) => void;

  /**
   * Whether the header is currently being dragged.
   */
  isDragging: boolean;

  /**
   * Ref for the portal container when dragging.
   */
  portalRef: RefObject<HTMLElement | null>;

  /**
   * Props to spread on the draggable element (undefined if drag-and-drop is disabled).
   */
  dragAndDropProps:
    | ({
        /**
         * Indicates that the element is draggable.
         */
        draggable: boolean;
        /**
         * Event handler for the drag start event.
         */
        onDragStart: (event: DragEvent<HTMLElement>) => void;
        /**
         * Event handler for the touch start event.
         */
        onTouchStart: (event: TouchEvent<HTMLElement>) => void;
        /**
         * Event handler for the touch move event.
         */
        onTouchMove: (event: TouchEvent<HTMLElement>) => void;
      } & DraggableAttributes)
    | undefined;
}

/**
 * Hook that encapsulates drag-and-drop functionality for header cells.
 *
 * @param params - Configuration for drag-and-drop behavior
 * @returns Drag-and-drop state and props to spread on the header element
 * @internal
 */
export function useHeaderCellDragAndDrop({
  headerId,
  column,
  columnIndex,
  columnDragAndDropMode,
}: UseHeaderCellDragAndDropParams): UseHeaderCellDragAndDropResult {
  const touchStart = useRef<TouchEvent>();
  const portalRef = useRef<HTMLElement>(null);

  const { setNodeRef, isDragging, attributes, listeners } = useSortable({
    id: headerId,
    data: {
      portalRef,
      column,
      columnIndex,
    },
  });

  const dragAndDropProps =
    columnDragAndDropMode === 'none'
      ? undefined
      : {
          ...attributes,
          draggable: true,
          onDragStart: (event: DragEvent<HTMLElement>) =>
            listeners?.onMouseDown(event),
          onTouchStart: (event: TouchEvent<HTMLElement>) => {
            touchStart.current = event;
          },
          onTouchMove: (event: TouchEvent<HTMLElement>) => {
            if (isDragMotion(event, touchStart?.current)) {
              listeners?.onTouchStart(event);
            }
          },
        };

  return {
    setNodeRef,
    isDragging,
    portalRef,
    dragAndDropProps,
  };
}
